/**
 * "Run on a case", start to finish: pick the case (unless the caller already
 * knows it), answer the skill's declared inputs, choose the mode, start.
 * Skills with no inputs skip straight from the case to the run. In `test`
 * mode the run is a Test: the mode is fixed to Plan First and it never acts.
 */
import { useEffect, useRef, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import type { CaseRef } from "@/features/caseIde/routes";

import type { SkillInput } from "@/features/firmKnowledge/types";
import { CasePickerDialog } from "./CasePickerDialog";
import {
  defaultInputValues,
  missingInputs,
  SkillInputsForm,
  type SkillInputValues
} from "./SkillInputsForm";

export type RunSkillRequest = {
  /** Absent when the caller supplied the case. */
  ref: CaseRef | null;
  inputs: SkillInputValues;
  mode: "agent" | "plan";
  /** Started from Test: Plan First, and never counted as automated. */
  test?: boolean;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skill: {
    title: string;
    inputs: SkillInput[];
    defaultMode: "agent" | "plan";
    /** The visas the skill is meant for; the case picker offers their cases first. */
    visas?: string[];
  } | null;
  /** When false the case is already known and the picker is skipped. */
  needsCase?: boolean;
  /** Test the skill: Plan First, fixed. */
  test?: boolean;
  onStart: (request: RunSkillRequest) => Promise<void> | void;
};

const TEST_NOTE =
  "A test plans the work and waits; it changes nothing on the case.";

export const RunSkillDialog: FC<Props> = ({
  open,
  onOpenChange,
  skill,
  needsCase = true,
  test = false,
  onStart
}) => {
  const inputs = skill?.inputs ?? [];
  const [ref, setRef] = useState<CaseRef | null>(null);
  const [caseLabel, setCaseLabel] = useState("");
  const [values, setValues] = useState<SkillInputValues>({});
  const [mode, setMode] = useState<"agent" | "plan">("agent");
  const [starting, setStarting] = useState(false);

  // Reset once per open (on the closed -> open edge), not on every render
  // while open, since callers pass the skill inline.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setRef(null);
      setCaseLabel("");
      setValues(defaultInputValues(inputs));
      setMode(test ? "plan" : skill?.defaultMode ?? "agent");
      setStarting(false);
    }
    wasOpen.current = open;
  }, [open, inputs, skill?.defaultMode, test]);

  const haveCase = !needsCase || ref !== null;
  const askInputs = inputs.length > 0;
  const verb = test ? "Test" : "Run";

  const start = async (pickedRef: CaseRef | null, picked: SkillInputValues) => {
    setStarting(true);
    try {
      await onStart({
        ref: pickedRef,
        inputs: picked,
        mode: test ? "plan" : mode,
        ...(test ? { test: true } : {})
      });
    } finally {
      setStarting(false);
    }
  };

  const pickCase = (picked: CaseRef, label: string) => {
    setRef(picked);
    setCaseLabel(label);
    if (!askInputs) void start(picked, defaultInputValues(inputs));
  };

  if (!skill) return null;

  // Step 1: the case.
  if (!haveCase) {
    return (
      <CasePickerDialog
        open={open}
        onOpenChange={onOpenChange}
        skillTitle={skill.title}
        visas={skill.visas}
        onPick={pickCase}
        test={test}
      />
    );
  }

  // Step 2: the inputs and mode. Reached directly when the case is known.
  if (!askInputs) {
    // Case known and nothing to ask: start on open. Rendered as an empty
    // dialog frame so the caller's open/close handling stays the same.
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-130">
          <DialogHeader>
            <DialogTitle>
              {verb} {skill.title}
            </DialogTitle>
            <DialogDescription>
              {test ? TEST_NOTE : "This automation has nothing to ask first."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void start(ref, {})}
              disabled={starting}
              data-testid="run-skill-start"
            >
              {starting ? <Spinner className="size-3.5" /> : null} {verb}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const missing = missingInputs(inputs, values);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-130">
        <DialogHeader>
          <DialogTitle>
            {verb} {skill.title}
          </DialogTitle>
          <DialogDescription>
            {caseLabel
              ? `On ${caseLabel}. A few things the automation needs first.`
              : "A few things the automation needs first."}
          </DialogDescription>
        </DialogHeader>
        <SkillInputsForm
          inputs={inputs}
          values={values}
          onChange={setValues}
          disabled={starting}
        />
        {test ? (
          <p className="text-sm text-fg-3" data-testid="run-skill-test-note">
            {TEST_NOTE}
          </p>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="run-skill-mode" className="text-sm">
              How It Runs
            </Label>
            <Select
              value={mode}
              onValueChange={(v) => setMode(v as "agent" | "plan")}
              disabled={starting}
            >
              <SelectTrigger id="run-skill-mode" data-testid="run-skill-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="agent">Do the Work</SelectItem>
                <SelectItem value="plan">
                  Plan First, Then Wait for Me
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={starting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void start(ref, values)}
            disabled={starting || missing.length > 0}
            data-testid="run-skill-start"
          >
            {starting ? <Spinner className="size-3.5" /> : null} {verb}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
